import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
} from 'rxjs';
import {
  Component,
  ChangeDetectionStrategy,
  Input,
  ViewChild,
  EventEmitter,
  Output,
  ChangeDetectorRef,
  SimpleChanges,
  OnChanges,
} from '@angular/core';

import { DprAttachmentsGridData } from '@shared/models/grid';
import {
  DprAttachmentsComponent,
  DprCommentComponent,
} from '@dpr-details/components';
import { Constants } from '@shared/utils';
import { Sync } from '@shared/models/grid';
import { SelectedSubproject } from '@shared/models';
import { GridApi } from '@grid/models';
import { SummaryTabs } from '@dpr-details/enums';
import {
  EditableComponent,
  OnlineDependentComponent,
} from '@dpr-details/models/component';

@UntilDestroy()
@Component({
  selector: 'app-dpr-comment-attachments',
  templateUrl: './dpr-comment-attachments.component.html',
  styleUrls: ['./dpr-comment-attachments.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprCommentAttachmentsComponent
  implements EditableComponent, OnlineDependentComponent, OnChanges
{
  //#region Templates

  @ViewChild(DprCommentComponent, { static: true })
  commentComponent: DprCommentComponent;

  @ViewChild(DprAttachmentsComponent, { static: true })
  attachmentComponent: DprAttachmentsComponent;

  //#endregion

  //#region Inputs

  @Input() gridData: DprAttachmentsGridData[];
  @Input() predefinedChanges: Sync<DprAttachmentsGridData>;
  @Input() selectedSubproject: SelectedSubproject;
  @Input() canEdit: boolean;
  @Input() comment: string;
  @Input() clientId: string;
  @Input() isAppOnline: boolean;
  @Input() tab: SummaryTabs;
  @Input() shouldDisplayPrintInReport = true;
  @Input() maxLength = Constants.FieldLimits.maxSummaryCommentLength;

  //#endregion

  //#region Outputs

  @Output() dataChanged = new EventEmitter();
  @Output() gridRegistered = new EventEmitter<
    GridApi<DprAttachmentsGridData>
  >();

  //#endregion

  fileUploadConfig = Constants.AttachmentFileUploadConfig;
  gridHasChanges$ = new BehaviorSubject<boolean>(false);

  constructor(private cdr: ChangeDetectorRef) {}

  //#region Lifecycle hooks

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.canEdit || changes.isAppOnline) {
      this.cdr.markForCheck();
    }
  }

  ngOnInit(): void {
    combineLatest([
      this.commentComponent.commentFormControl.valueChanges,
      this.gridHasChanges$,
    ])
      .pipe(
        untilDestroyed(this),
        filter(
          ([commentChanges, hasGridChanges]) =>
            commentChanges !== this.comment || hasGridChanges
        ),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.dataChanged.emit();
      });
  }

  //#endregion

  onAttachmentsChanged(): void {
    this.gridHasChanges$.next(true);
  }

  onGridRegistered(gridApi: GridApi<DprAttachmentsGridData>): void {
    this.gridRegistered.emit(gridApi);
  }
}
